import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/Toast";
import { Button, Card, Select } from "../../components/ui";
import { ConfirmModal } from "../../components/ui/Modal";

const MACROCATEGORIE = [
  { value: 100000000, label: "Manutenzione" },
  { value: 100000001, label: "Servizi" },
  { value: 100000002, label: "Forniture" },
  { value: 100000003, label: "Consulenza" },
];

const TIPI_DOCUMENTO = [
  { value: 100000005, label: "Certificazione" },
  { value: 100000006, label: "Polizza" },
  { value: 100000007, label: "DURC" },
  { value: 100000008, label: "Visura" },
  { value: 100000011, label: "Altro" },
];

const tipoLabel = (v) =>
  TIPI_DOCUMENTO.find((t) => t.value === v)?.label || `Tipo ${v}`;

function ConfigDocumentiValutazionePage() {
  const { apiRequest, user } = useAuth();
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState(MACROCATEGORIE[0].value);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [newTipo, setNewTipo] = useState("");
  const [newObbligatorio, setNewObbligatorio] = useState(true);
  const [newPeso, setNewPeso] = useState("1.0");

  const isAdmin = user?.ruolo === 100000000;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest(
        `/config-documenti-valutazione?macrocategoria=${activeTab}`,
      );
      // Solo righe macrocategoria-level (no override fornitore)
      const macroRows = (data || []).filter((r) => !r._phc_fornitore_value);
      setRows(macroRows);
    } catch (error) {
      showToast(
        error.message || "Errore nel caricamento configurazione",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [activeTab, apiRequest, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const tipiNonConfigurati = TIPI_DOCUMENTO.filter(
    (t) => !rows.some((r) => r.phc_tipodocumento === t.value),
  );

  const handleAdd = async () => {
    if (!newTipo) {
      showToast("Seleziona un tipo documento", "error");
      return;
    }
    setSaving(true);
    try {
      await apiRequest("/config-documenti-valutazione", {
        method: "POST",
        body: JSON.stringify({
          macrocategoria: activeTab,
          tipodocumento: parseInt(newTipo),
          obbligatorio: newObbligatorio,
          peso: parseFloat(newPeso) || 1.0,
          attivo: true,
        }),
      });
      showToast("Configurazione aggiunta", "success");
      setNewTipo("");
      setNewObbligatorio(true);
      setNewPeso("1.0");
      loadData();
    } catch (error) {
      showToast(error.message || "Errore", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (row, patch) => {
    try {
      await apiRequest(
        `/config-documenti-valutazione/${row.phc_configdocumentivalutazioneid}`,
        {
          method: "PUT",
          body: JSON.stringify(patch),
        },
      );
      loadData();
    } catch (error) {
      showToast(error.message || "Errore aggiornamento", "error");
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiRequest(
        `/config-documenti-valutazione/${confirmDelete.phc_configdocumentivalutazioneid}`,
        { method: "DELETE" },
      );
      showToast("Eliminato", "success");
      setConfirmDelete(null);
      loadData();
    } catch (error) {
      showToast(error.message || "Errore eliminazione", "error");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Configurazione Documenti Valutazione
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Definisci quali tipi documento sono obbligatori per ogni
          macrocategoria fornitore. Influenza il KPI "Completezza documentale".
        </p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {MACROCATEGORIE.map((m) => (
            <button
              key={m.value}
              onClick={() => setActiveTab(m.value)}
              className={`py-2 px-1 border-b-2 text-sm font-medium ${
                activeTab === m.value
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {m.label}
            </button>
          ))}
        </nav>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">
                  Tipo Documento
                </th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 w-32">
                  Obbligatorio
                </th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 w-32">
                  Peso
                </th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 w-24">
                  Attivo
                </th>
                <th className="text-right px-4 py-3 font-medium text-gray-600 w-24">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-6 text-gray-400">
                    Caricamento...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-6 text-gray-400">
                    Nessuna configurazione per questa macrocategoria
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.phc_configdocumentivalutazioneid}
                    className="border-b hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 font-medium">
                      {tipoLabel(r.phc_tipodocumento)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        disabled={!isAdmin}
                        checked={!!r.phc_obbligatorio}
                        onChange={(e) =>
                          handleUpdate(r, { obbligatorio: e.target.checked })
                        }
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        disabled={!isAdmin}
                        defaultValue={r.phc_peso}
                        onBlur={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!isNaN(v) && v !== r.phc_peso) {
                            handleUpdate(r, { peso: v });
                          }
                        }}
                        className="w-20 text-center rounded border-gray-300 text-xs"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        disabled={!isAdmin}
                        checked={!!r.phc_attivo}
                        onChange={(e) =>
                          handleUpdate(r, { attivo: e.target.checked })
                        }
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => setConfirmDelete(r)}
                        >
                          Elimina
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {isAdmin && tipiNonConfigurati.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold text-gray-900 mb-3">
            Aggiungi tipo documento
          </h3>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px]">
              <label className="block text-xs text-gray-500 mb-1">
                Tipo documento
              </label>
              <Select
                value={newTipo}
                onChange={(e) => setNewTipo(e.target.value)}
              >
                <option value="">Seleziona...</option>
                {tipiNonConfigurati.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newObbligatorio}
                onChange={(e) => setNewObbligatorio(e.target.checked)}
                className="h-4 w-4"
              />
              Obbligatorio
            </label>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Peso</label>
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={newPeso}
                onChange={(e) => setNewPeso(e.target.value)}
                className="w-24 rounded border-gray-300 text-sm"
              />
            </div>
            <Button onClick={handleAdd} disabled={saving || !newTipo}>
              {saving ? "..." : "Aggiungi"}
            </Button>
          </div>
        </Card>
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Elimina configurazione"
        message={
          confirmDelete
            ? `Eliminare ${tipoLabel(confirmDelete.phc_tipodocumento)} dalla macrocategoria? L'operazione e' irreversibile.`
            : ""
        }
        confirmText="Elimina"
        variant="danger"
      />
    </div>
  );
}

export default ConfigDocumentiValutazionePage;
